import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== ICONS ====================
const AcademicCapIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
  </svg>
);

const HeartIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
  </svg>
);

const SparklesIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
    {eyebrow && (
      <div className={align === "center" ? "flex justify-center" : ""}>
        <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
      {title}
    </h2>
    {description && (
      <p className={`mt-4 text-base md:text-lg text-gray-600 leading-7 ${align === "center" ? "mx-auto" : ""}`}>
        {description}
      </p>
    )}
  </div>
);

const ServiceCard = ({ icon, title, description }) => (
  <div className="flex flex-col items-center text-center p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-school-blue/8 text-school-blue mb-6 shadow-sm">
      {icon}
    </div>
    <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 mb-3 tracking-tight leading-tight">
      {title}
    </h3>
    <p className="text-sm md:text-base text-gray-600 leading-7 max-w-sm mx-auto">{description}</p>
  </div>
);

const ServiceTeamCard = ({ title, staff }) => (
  <div className="p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300">
    <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 mb-6 tracking-tight pb-4 border-b border-gray-100">
      {title}
    </h3>
    <div className="space-y-3">
      {staff.map((name, index) => (
        <div key={index} className="flex items-center gap-3 text-gray-700">
          <div className="w-2 h-2 rounded-full bg-school-gold flex-shrink-0" />
          <span className="text-sm md:text-base">{name}</span>
        </div>
      ))}
    </div>
  </div>
);

// ==================== MAIN COMPONENT ====================

const RelatedServices = () => {
  const services = [
    {
      icon: <AcademicCapIcon />,
      title: "Academic Support",
      description: "RTI teachers and IEP specialists providing individualized educational support for all students."
    },
    {
      icon: <HeartIcon />,
      title: "Therapeutic Services",
      description: "Speech therapy, occupational therapy, physical therapy, and adaptive physical education."
    },
    {
      icon: <SparklesIcon />,
      title: "Language Services",
      description: "English as a New Language (ENL) support for multilingual learners."
    }
  ];

  const serviceTeams = [
    {
      title: "RTI Teachers",
      staff: ["D. Carey", "J. Bondi", "S. Woods"]
    },
    {
      title: "IEP Teacher",
      staff: ["S. Holbrook"]
    },
    {
      title: "Speech Therapy",
      staff: ["T. Noto", "M. Mercado", "M. Martin", "A. Sorrentini"]
    },
    {
      title: "OT/PT/APE",
      staff: ["L. Marquez", "J. Calderon", "S. Kovacs"]
    },
    {
      title: "ENL (English as a New Language)",
      staff: ["R. Cruz", "J. Alonzo", "L. Teron", "H. Moran-Silva"]
    }
  ];

  return (
    <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* ==================== HERO SECTION ==================== */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

          <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
            <div className="max-w-4xl bg-[#0F2240]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
                <span className="w-2 h-2 rounded-full bg-school-gold" aria-hidden="true" />
                Student Services
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
                Related Services
              </h1>

              <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
                Comprehensive support services to help every student succeed
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== SERVICES OVERVIEW ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Our Services"
              title="Comprehensive Student Support"
              description="We provide a wide range of specialized services to support the diverse needs of our students"
            />

            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {services.map((service, index) => (
                <ServiceCard key={index} {...service} />
              ))}
            </div>
          </Container>
        </section>

        {/* ==================== SERVICE TEAMS ==================== */}
        <section className="py-20 md:py-28 bg-[#EDE5D0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Our Teams"
              title="Meet Our Service Providers"
              description="Dedicated professionals providing specialized support across multiple disciplines"
            />

            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {serviceTeams.map((team, index) => (
                <ServiceTeamCard key={index} {...team} />
              ))}
            </div>
          </Container>
        </section>

        {/* ==================== CTA SECTION ==================== */}
        <section className="relative py-24 md:py-32 bg-school-gold overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-school-blue/8 rounded-full blur-3xl pointer-events-none" />

          <Container className="relative text-center">
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-school-blue mb-6">
              Questions About Services?
            </h2>
            <p className="text-xl text-school-blue max-w-2xl mx-auto mb-10 font-light">
              Our team is here to help. Contact us to learn more about our related services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/coming-soon"
                className="px-8 py-4 rounded-full bg-school-blue text-white font-bold hover:bg-[#0F2240] transition-colors shadow-soft"
              >
                Contact Us
              </a>
              <a
                href="/"
                className="px-8 py-4 rounded-full bg-white/30 text-school-blue font-medium border-2 border-school-blue/20 hover:bg-white/50 transition-colors"
              >
                Back to Home
              </a>
            </div>
          </Container>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default RelatedServices;
